import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {defer, interval} from 'rxjs';
import {concatMap, last, takeWhile} from 'rxjs/operators';
import {ReportAdminService} from '../../services/api/report-admin.service';
import {AdminNavigationService} from '../../services/admin-navigation.service';
import {RecentAccountActivityReportDataViewModel} from '../../viewmodels/recentAccountActivityReportDataViewModel';
import {DateFormat, Dates, LoadingState} from 'common';
import {BaseComponent} from '../base.component';
import {DeviceType} from '../../../../../common/src/lib/models/deviceType';

@Component({
    selector: 'app-report',
    templateUrl: './recent-account-activity-report-overview.component.html',
    styleUrls: ['./recent-account-activity-report-overview.component.scss']
})

export class RecentAccountActivityReportOverviewComponent extends BaseComponent implements OnInit {
    public loadingState: LoadingState;
    public loadingProgress: number;
    public loadingStatus: string;
    public dateCreated: string;
    public viewModels: RecentAccountActivityReportDataViewModel[];

    constructor(private route: ActivatedRoute,
                private reportAdminService: ReportAdminService,
                private adminNavigation: AdminNavigationService) {
        super();
        this.loadingState = LoadingState.LOADING;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-RecentAccountActivityReport', '', '/admin/recentAccountActivityReportOverview');

        this.loadingState = LoadingState.LOADING;

        this.runReportUntilCompleted();
    }

    public onExportCsvPressed(): void {
        const csvData = this.convertToCSV(this.viewModels);
        this.downloadCSV(csvData, this.dateCreated + '_report.csv');
    }

    private convertToCSV(viewModels: RecentAccountActivityReportDataViewModel[]): string {
        if (!viewModels || viewModels.length === 0) {
            return '';
        }

        const header = ['Name',
            'Mail',
            'Phone',
            'Specialty',
            'Created',
            'Country',
            'Postal Code',
            'Subscription',
            'Subscription Period',
            'Subscription Source',
            'Subscription Author',
            'Next Payment',
            'Last Payment',
            'Expiration Date',
            'Total number of payments',
            'Total payments Amount',
            'Sessions 1 week',
            'Sessions 1 Month',
            'Sessions 3 Months',
            'Sessions 6 Months',
            'Sessions 12 Months',
            'Total Sessions',
            'Last Used',
            'Serial Codes',
            'Grip',
            'Muscle',
            'Plates',
            'Sens',
            'Bubble',
            'Link',
            'Deltas',
            'Myo',
            'K Power',
            '3D Deltas',
            'Total Grip',
            'Total Muscle',
            'Total Plates',
            'Total Sens',
            'Total Bubble',
            'Total Link',
            'Total Deltas',
            'Total Myo',
            'Total K Power',
            'Total 3D Deltas',
            'Account Code',
            'Subscription Notes',
            'Account Role'];
        const rows = viewModels.map(acc => [
            this.formatValueEscapeCsvCharacters(acc.fullName),
            this.formatValueEscapeCsvCharacters(acc.email),
            this.formatValueEscapeCsvCharacters(acc.phone),
            this.formatValueEscapeCsvCharacters(acc.specialty),
            this.formatDate(acc.createdOnDate),
            this.formatValueEscapeCsvCharacters(acc.country),
            this.formatValueEscapeCsvCharacters(acc.zipCode),
            this.formatValue(acc.subscription),
            this.formatValue(acc.subscriptionPeriodUnit),
            this.formatValue(acc.subscriptionSource),
            this.formatValue(acc.subscriptionAuthor),
            this.formatDate(acc.nextPaymentDate),
            this.formatDate(acc.lastPaymentDate),
            this.formatDate(acc.expirationDate),
            this.formatValue(acc.totalNumberOfPayments),
            this.formatValue(acc.totalPaymentsAmount),
            this.formatValue(acc.sessionsOneWeek),
            this.formatValue(acc.sessionsOneMonth),
            this.formatValue(acc.sessionsThreeMonths),
            this.formatValue(acc.sessionsSixMonths),
            this.formatValue(acc.sessionsYear),
            this.formatValue(acc.totalSessions),
            this.formatDate(acc.lastUsedDate),
            this.formatValue(acc.serialCodes),
            // 3 months old sessions
            this.formatValue(acc[DeviceType.GRIP.toLowerCase()]),
            this.formatValue(acc[DeviceType.MUSCLE_TESTER.toLowerCase()]),
            this.formatValue(acc[DeviceType.PLATES.toLowerCase()]),
            this.formatValue(acc[DeviceType.SENS.toLowerCase()]),
            this.formatValue(acc[DeviceType.BUBBLE.toLowerCase()]),
            this.formatValue(acc[DeviceType.LINK.toLowerCase()]),
            this.formatValue(acc[DeviceType.HEXAS.toLowerCase()]),
            this.formatValue(acc[DeviceType.EMG.toLowerCase()]),
            this.formatValue(acc[DeviceType.KPOWER.toLowerCase()]),
            this.formatValue(acc[DeviceType.DELTAS_3D.toLowerCase()]),
            // Since creation sessions
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.GRIP.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.MUSCLE_TESTER.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.PLATES.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.SENS.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.BUBBLE.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.LINK.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.HEXAS.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.EMG.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.KPOWER.toLowerCase())]),
            this.formatValue(acc[RecentAccountActivityReportDataViewModel.totalSessionsKey(DeviceType.DELTAS_3D.toLowerCase())]),
            this.formatValue(acc.code),
            this.formatValueEscapeCsvCharacters(acc.notes),
            this.formatValue(acc.accountRole)
        ]);

        // Combine header and rows into CSV format
        return [header, ...rows].map(e => e.join(',')).join('\n');
    }

    // Create a Blob and trigger download
    private downloadCSV(csvContent: string, filename: string): void {
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const anchor = document.createElement('a');
        anchor.href = URL.createObjectURL(blob);
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    public onNavigateToReportTableView(): void {
        this.adminNavigation.goToReportHomeTableView();
    }

    /** Periodically request the status of the report and if not completed show the results in the loadingStatus, loadingProgress variables. <br>
     *  When completed show the data.<br><br>
     *  Due to the recent account activity large response size, we need to wait until the request is processed and then retry the request.<br>
     *  If the request is being processed and data bytes are being transferred, then do not re-request.
     */
    private runReportUntilCompleted(): void {
        const intervalSeconds = 8; // Interval in seconds

        interval(intervalSeconds * 1000)
            .pipe(
                concatMap(() => defer(this.reportAdminService.getRecentAccountActivity)),
                takeWhile(response => {
                    // Check the response and append the progress and continue making requests if condition not met.
                    this.loadingProgress = response.reportStatus.progress;
                    this.loadingStatus = response.reportStatus.progress.toString();
                    return response.reportStatus.progress !== 100;
                }, true),
                last()
            )
            .subscribe(
                (response) => {
                    // Handle the response
                    const status = response?.reportStatus;

                    if (status !== undefined && status !== null &&
                            status.dateCreated !== 0 && status.progress === 100) {

                        // display results
                        this.viewModels = RecentAccountActivityReportDataViewModel.from(response.rows);
                        const date = new Date(status.dateCreated);
                        this.dateCreated = Dates.format(date, DateFormat.DATE_SHORTER);

                        this.loadingState = LoadingState.LOADED;
                    }
                },
                error => {
                    this.errorHandler.handle(`Could not create report. ${error.statusText} (${error.status}`);
                    this.notification.info(`Could not create report. ${error.statusText} (${error.status}`);
                    this.loadingState = LoadingState.ERROR;
                }
            );
    }

    private formatValue(value: any): string {
        if (value === undefined || value === null || value === 0 || value === '') {
            return '-';
        }

        return value.toString();
    }

    // Escaping commas ( , ) and new lines ( \n )
    private formatValueEscapeCsvCharacters(value: string): string {
        if (value === undefined || value === null || value === '') {
            return '-';
        }

        return value.replace(/[,\n]/g, ' ');
    }

    private formatDate(value: number): string {
        if (value === undefined || value === null || value === 0) {
            return '-';
        }

        const date = new Date(value);
        return Dates.format(date, DateFormat.DATE_LONG);
    }
}
